import { useEffect } from "react"

interface BodyScrollLockOptions {
  /** Restore the scroll position captured when this lock was taken */
  preserveScrollPosition?: boolean
}

interface BodyStyleSnapshot {
  overflow: string
  paddingRight: string
}

let activeLocks = 0
let snapshot: BodyStyleSnapshot | null = null

const lockBody = () => {
  const { body, documentElement } = document

  snapshot = { overflow: body.style.overflow, paddingRight: body.style.paddingRight }

  body.style.overflow = "hidden"

  // Compensate for the scrollbar the lock removes so the page does not shift
  const scrollbarWidth = window.innerWidth - documentElement.clientWidth
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`
  }
}

const unlockBody = () => {
  if (!snapshot) {
    return
  }

  document.body.style.overflow = snapshot.overflow
  document.body.style.paddingRight = snapshot.paddingRight
  snapshot = null
}

/**
 * Reference-counted body scroll lock. Stacked sheets can hold a lock at the same time; the
 * page is unlocked once the last holder releases, and the holder that releases last decides
 * whether the scroll position it captured is restored.
 */
const acquireBodyScrollLock = (preserveScrollPosition: boolean): (() => void) => {
  const scrollY = window.scrollY

  if (activeLocks === 0) {
    lockBody()
  }

  activeLocks += 1
  let isReleased = false

  return () => {
    if (isReleased) {
      return
    }

    isReleased = true
    activeLocks -= 1

    if (activeLocks > 0) {
      return
    }

    unlockBody()

    if (preserveScrollPosition) {
      window.scrollTo({ top: scrollY, behavior: "auto" })
    }
  }
}

export const useBodyScrollLock = (isLocked: boolean, { preserveScrollPosition = true }: BodyScrollLockOptions = {}) => {
  useEffect(() => {
    if (!isLocked) {
      return
    }

    return acquireBodyScrollLock(preserveScrollPosition)
  }, [isLocked, preserveScrollPosition])
}
